import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import {
  Card, Field, Input, Textarea, PageHeader, PrimaryButton, SecondaryButton, DangerButton,
} from "@/components/admin/AdminUI";
import { SlugConflictWarning } from "@/components/admin/SlugConflictWarning";
import { SeoGenerateButton } from "@/components/admin/SeoGenerateButton";
import { RefineEntriesModal } from "@/components/admin/RefineEntriesModal";
import { OurTakeGenerateModal } from "@/components/admin/OurTakeGenerateModal";
import { OurTakeImagesField } from "@/components/admin/OurTakeImagesField";
import { MultiSelect } from "@/components/public/MultiSelect";
import { slugify } from "@/lib/slugify";

const EMPTY = {
  name: "", website_url: "", crawler_start_url: "", description_short: "",
  country: "", region: "", logo_url: "", primary_material_focus: "",
  is_public: true, is_featured: false, crawler_enabled: true,
  our_take: "", our_take_images: [],
  sustainability_claims: [], warnings: [], certification_ids: [],
};

export default function AdminBrandEdit() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [refineKind, setRefineKind] = useState(null); // "claims" | "warnings" | null
  const [refining, setRefining] = useState(false);
  const [refineResult, setRefineResult] = useState({ refined: [], new: [] });
  const [refineOpen, setRefineOpen] = useState(false);
  const [ourTakeOpen, setOurTakeOpen] = useState(false);

  async function runRefine(kind) {
    if (!id) {
      toast.error("Save the brand first before refining with AI");
      return;
    }
    const entries = kind === "claims" ? form.sustainability_claims : form.warnings;
    if ((!entries || entries.length === 0) && kind === "warnings") {
      toast("Hint: AI may also suggest brand-new entries to add.");
    }
    setRefining(true);
    setRefineKind(kind);
    const t = toast.loading(`Refining ${kind} with AI…`);
    try {
      const res = await api.post(`/admin/brands/${id}/refine-entries`, { kind });
      setRefineResult({ refined: res.data?.refined || [], new: res.data?.new || [] });
      toast.dismiss(t);
      const editsCount = (res.data?.refined || []).filter(Boolean).length;
      const newCount = (res.data?.new || []).length;
      if (editsCount + newCount === 0) {
        toast.success("Nothing to refine — your entries look good.");
      } else {
        setRefineOpen(true);
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : `Refining ${kind} failed`, { id: t });
    } finally {
      setRefining(false);
    }
  }

  function applyRefineEdit(index, suggested) {
    const key = refineKind === "claims" ? "sustainability_claims" : "warnings";
    setForm((f) => {
      const next = [...(f[key] || [])];
      next[index] = { ...next[index], ...suggested };
      return { ...f, [key]: next };
    });
    toast.success("Edit applied — click Save to persist");
  }

  function applyRefineNew(suggested) {
    const key = refineKind === "claims" ? "sustainability_claims" : "warnings";
    setForm((f) => ({ ...f, [key]: [...(f[key] || []), suggested] }));
    toast.success("New entry added — click Save to persist");
  }

  const { data: brand } = useQuery({
    queryKey: ["admin-brand", id],
    queryFn: async () => (await api.get(`/admin/brands/${id}`)).data,
    enabled: !!id,
  });

  useEffect(() => {
    if (brand) setForm({ ...EMPTY, ...brand });
  }, [brand]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : (e.target ? e.target.value : e) }));

  async function save() {
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post("/admin/brands", form);
        toast.success("Brand created");
        navigate(`/admin/brands/${res.data.id}`);
      } else {
        await api.patch(`/admin/brands/${id}`, form);
        toast.success("Brand saved");
        qc.invalidateQueries({ queryKey: ["admin-brand", id] });
        qc.invalidateQueries({ queryKey: ["admin-brands"] });
      }
    } catch (e) {
      const data = e?.response?.data;
      const det = data?.detail;
      if (det && typeof det === "object" && det.error === "slug_conflict") {
        const what = det.taken_by === "reserved" ? "a reserved system path" : det.taken_by === "brand" ? "another brand" : "a CMS page";
        toast.error(`Slug "${form.slug}" is already taken by ${what}${det.name ? ` ("${det.name}")` : ""}. Choose a different slug.`);
      } else {
        toast.error(typeof det === "string" ? det : "Save failed");
      }
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!window.confirm("Delete this brand AND all its clothing items, crawler results, crawl runs, and false-advertising reports? This cannot be undone.")) return;
    try {
      const res = await api.delete(`/admin/brands/${id}`);
      const d = res.data?.deleted || {};
      const items = d.clothing_items ?? 0;
      toast.success(
        `Brand deleted — also removed ${items} clothing item${items === 1 ? "" : "s"}, ${d.crawler_results ?? 0} crawler result${(d.crawler_results ?? 0) === 1 ? "" : "s"}, ${d.crawl_runs ?? 0} crawl run${(d.crawl_runs ?? 0) === 1 ? "" : "s"}, and ${d.false_advertising_reports ?? 0} report${(d.false_advertising_reports ?? 0) === 1 ? "" : "s"}.`,
        { duration: 6000 }
      );
      // Invalidate every query that could be showing stale data
      qc.invalidateQueries({ queryKey: ["admin-brands"] });
      qc.invalidateQueries({ queryKey: ["admin-clothing"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
      qc.invalidateQueries({ queryKey: ["reports"] });
      qc.invalidateQueries({ queryKey: ["crawl-runs"] });
      qc.invalidateQueries({ queryKey: ["home"] });
      qc.invalidateQueries({ queryKey: ["brands"] });
      qc.invalidateQueries({ queryKey: ["clothing"] });
      navigate("/admin/brands");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div data-testid="admin-brand-edit">
      <RefineEntriesModal
        open={refineOpen}
        onOpenChange={setRefineOpen}
        title={refineKind === "claims" ? "Refine sustainability claims with AI" : "Refine warnings with AI"}
        refined={refineResult.refined}
        newEntries={refineResult.new}
        onApplyEdit={applyRefineEdit}
        onAddNew={applyRefineNew}
      />
      <OurTakeGenerateModal
        open={ourTakeOpen}
        onOpenChange={setOurTakeOpen}
        brandId={id}
        currentText={form.our_take || ""}
        onApprove={(text) => setForm((f) => ({ ...f, our_take: text }))}
      />
      <PageHeader
        title={isNew ? "New brand" : `Edit: ${form.name || "..."}`}
        actions={
          <>
            <PrimaryButton onClick={save} disabled={saving} data-testid="brand-save-button">{saving ? "Saving…" : "Save"}</PrimaryButton>
            {!isNew && <DangerButton onClick={remove}>Delete</DangerButton>}
          </>
        }
      />

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Section 1 — Content from the brand */}
          <Card className="space-y-4">
            <div className="text-[11px] uppercase tracking-wider text-[#6C757D] -mb-1">1. Content from the brand</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name"><Input value={form.name} onChange={set("name")} data-testid="brand-name-input" /></Field>
              <Field label="Primary material focus"><Input value={form.primary_material_focus || ""} onChange={set("primary_material_focus")} /></Field>
              <Field label="Website URL"><Input value={form.website_url || ""} onChange={set("website_url")} data-testid="brand-website-input" /></Field>
              <Field label="Crawler start URL"><Input value={form.crawler_start_url || ""} onChange={set("crawler_start_url")} placeholder="e.g. https://brand.com/shop" data-testid="brand-crawler-url-input" /></Field>
              <Field label="Country"><Input value={form.country || ""} onChange={set("country")} /></Field>
              <Field label="Region"><Input value={form.region || ""} onChange={set("region")} /></Field>
              <Field label="Logo URL"><Input value={form.logo_url || ""} onChange={set("logo_url")} /></Field>
            </div>
            <Field label="From the brand" hint="Tagline/lede shown on cards and on the brand page. Pulled or paraphrased from the brand's website."><Textarea rows={3} value={form.description_short || ""} onChange={set("description_short")} /></Field>
          </Card>

          {/* Section 2 — Our take (public editorial opinion + gallery) */}
          <Card className="space-y-4">
            <div className="flex items-center justify-between gap-2 -mb-1">
              <div className="text-[11px] uppercase tracking-wider text-[#6C757D]">2. Our take (public)</div>
              <button
                type="button"
                onClick={() => setOurTakeOpen(true)}
                disabled={!id}
                title={!id ? "Save the brand first" : "Generate the editorial take with AI"}
                className="inline-flex items-center gap-1 text-xs font-medium text-leaf hover:underline disabled:opacity-50 disabled:no-underline"
                data-testid="our-take-ai-button"
              >
                ✨ Add with AI
              </button>
            </div>
            <Field
              label="Editorial opinion"
              hint="Your public editorial take on this brand — 3–5 sentences. Shown on the brand page below the brand's own blurb."
            >
              <Textarea
                rows={5}
                value={form.our_take || ""}
                onChange={set("our_take")}
                data-testid="brand-our-take-input"
                placeholder="What you think of this brand — honest, journalistic, no marketing fluff."
              />
            </Field>
            <OurTakeImagesField
              brandId={id}
              value={form.our_take_images || []}
              onChange={(next) => setForm((f) => ({ ...f, our_take_images: next }))}
            />
          </Card>

          {/* Section 3 — Third-party evidence (claims, warnings, certifications) */}
          <Card className="space-y-4">
            <div className="text-[11px] uppercase tracking-wider text-[#6C757D] -mb-1">3. Publicly available claims, warnings &amp; certifications</div>

            <BrandCertificationsField value={form.certification_ids || []} onChange={(next) => setForm((f) => ({ ...f, certification_ids: next }))} />

            <RefList
              title="Sustainability claims"
              description="Statements the brand makes about its sustainability — with a link to where it's published (their site, certification body, press, etc.) for full transparency."
              items={form.sustainability_claims || []}
              onChange={(next) => setForm((f) => ({ ...f, sustainability_claims: next }))}
              testid="claims"
              refineLabel={refining && refineKind === "claims" ? "Refining…" : "✨ Refine with AI"}
              onRefine={!isNew ? () => runRefine("claims") : null}
              refineDisabled={refining}
            />

            <RefList
              title="Warnings"
              description="Third-party references showing the brand has been called out for greenwashing, lawsuits, debunked claims, or other non-sustainable practices. Each needs a link to the source."
              items={form.warnings || []}
              onChange={(next) => setForm((f) => ({ ...f, warnings: next }))}
              testid="warnings"
              refineLabel={refining && refineKind === "warnings" ? "Refining…" : "✨ Refine with AI"}
              onRefine={!isNew ? () => runRefine("warnings") : null}
              refineDisabled={refining}
            />
          </Card>
        </div>

        <Card className="space-y-4 h-fit">
          <div className="flex items-center justify-between">
            <span className="text-sm">Public</span>
            <input type="checkbox" checked={!!form.is_public} onChange={set("is_public")} data-testid="brand-public-toggle" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Featured</span>
            <input type="checkbox" checked={!!form.is_featured} onChange={set("is_featured")} data-testid="brand-featured-toggle" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Crawler enabled</span>
            <input type="checkbox" checked={!!form.crawler_enabled} onChange={set("crawler_enabled")} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm" title="Force Playwright headless browser even when httpx succeeds. Slower but needed for JS-rendered SPAs (Shopify Hydrogen, Next.js storefronts, etc.)">
              Force browser crawler
            </span>
            <input type="checkbox" checked={!!form.force_browser_crawler} onChange={set("force_browser_crawler")} data-testid="brand-force-browser-toggle" />
          </div>
          {!isNew && brand && (
            <div className="text-xs text-[#6C757D] pt-3 border-t border-[#E9ECEF] space-y-1">
              <div>Score: <strong>{brand.sustainability_score ?? "—"}{brand.sustainability_score != null ? "%" : ""}</strong></div>
              <div>Reviewed: {brand.total_reviewed_items || 0}</div>
              <div>Approved: {brand.total_approved_items || 0}</div>
              <div>Rejected: {brand.total_rejected_items || 0}</div>
              <div>Pending: {brand.total_pending_items || 0}</div>
              <div>False ad flags: {brand.false_advertising_flags_count || 0}</div>
            </div>
          )}

          {form.logo_url && (
            <div className="pt-3 border-t border-[#E9ECEF]">
              <h3 className="text-xs uppercase tracking-wider text-[#6C757D] mb-2">Logo preview</h3>
              <img src={form.logo_url} alt="" className="w-full rounded border border-[#E9ECEF] bg-white" data-testid="brand-logo-preview" />
            </div>
          )}

          <div className="pt-3 border-t border-[#E9ECEF]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wider text-[#6C757D]">SEO</h3>
              <SeoGenerateButton
                disabled={!form.name}
                buildPayload={() => ({
                  kind: "brand",
                  name: form.name,
                  country: form.country,
                  short_description: form.description_short,
                  why_chosen: form.our_take,
                })}
                onResult={({ seo_title, seo_description }) =>
                  setForm((f) => ({ ...f, seo_title, seo_description }))
                }
              />
            </div>
            <Field label="SEO title" hint="Used in browser tab + Google results. Falls back to the brand name."><Input value={form.seo_title || ""} onChange={set("seo_title")} maxLength={120} /></Field>
            <Field label="SEO description" hint="155–160 chars ideally. Falls back to the short description."><Textarea rows={2} value={form.seo_description || ""} onChange={set("seo_description")} maxLength={300} /></Field>
            <p className="text-[11px] text-[#6C757D] mt-1">URL: <code>/brands/{form.slug || slugify(form.name) || "<slug>"}</code> (automatic)</p>
            <SlugConflictWarning slug={form.slug || slugify(form.name)} kind="brand" ignoreId={id} urlPrefix="/brands" />
          </div>
        </Card>
      </div>
    </div>
  );
}

// Editor for a list of {title, url, description} entries used by both the
// "Sustainability claims" and "Warnings" sections on a brand.
// Multi-select for brand (company-level) certifications. Fetches the admin
// brand-cert list and renders a labeled MultiSelect. Stored as an array of
// brand_certifications IDs.
function BrandCertificationsField({ value, onChange }) {
  const { data: certs = [] } = useQuery({
    queryKey: ["admin-brand-certifications"],
    queryFn: async () => (await api.get("/admin/brand-certifications")).data,
  });
  return (
    <div className="border-t border-[#E9ECEF] pt-4">
      <h3 className="text-xs uppercase tracking-wider text-[#6C757D] mb-1">Certifications held by this brand</h3>
      <p className="text-[11px] text-[#6C757D] mb-2">Company-level certifications only (B Corp, Fair Trade USA, 1% for the Planet, etc.). Material/product certifications live on individual clothing items.</p>
      <MultiSelect
        testId="brand-certifications-select"
        placeholder="No certifications selected"
        options={certs.map((c) => ({ id: c.id, label: c.name }))}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

function RefList({ title, description, items, onChange, testid, onRefine, refineLabel, refineDisabled }) {
  function add() {
    onChange([...(items || []), { title: "", url: "", description: "" }]);
  }
  function update(idx, key, value) {
    const next = items.map((it, i) => i === idx ? { ...it, [key]: value } : it);
    onChange(next);
  }
  function remove(idx) {
    onChange(items.filter((_, i) => i !== idx));
  }
  function move(idx, delta) {
    const next = items.slice();
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  }
  return (
    <div className="border-t border-[#E9ECEF] pt-4">
      <div className="flex items-center justify-between mb-1 gap-2">
        <h3 className="text-xs uppercase tracking-wider text-[#6C757D]">{title}</h3>
        <div className="flex items-center gap-2">
          {onRefine && (
            <button
              type="button"
              onClick={onRefine}
              disabled={refineDisabled}
              className="inline-flex items-center gap-1 text-xs font-medium text-leaf hover:underline disabled:opacity-50 disabled:no-underline"
              data-testid={`${testid}-refine`}
              title="Fetch each source URL and ask AI to suggest improvements + new entries"
            >
              {refineLabel || "✨ Refine with AI"}
            </button>
          )}
          <SecondaryButton onClick={add} data-testid={`${testid}-add`}>+ Add</SecondaryButton>
        </div>
      </div>
      <p className="text-[11px] text-[#6C757D] mb-3">{description}</p>
      {items.length === 0 && (
        <div className="text-xs text-[#6C757D] italic">No entries yet.</div>
      )}
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="border border-[#E9ECEF] rounded-md p-3 space-y-2" data-testid={`${testid}-row-${i}`}>
            <div className="grid sm:grid-cols-[1fr_1fr_200px_auto] gap-2">
              <Input
                value={it.title || ""}
                onChange={(e) => update(i, "title", e.target.value)}
                placeholder="Title (e.g. '100% GOTS-certified organic cotton')"
                data-testid={`${testid}-title-${i}`}
              />
              <Input
                value={it.url || ""}
                onChange={(e) => update(i, "url", e.target.value)}
                placeholder="https://link-to-source"
                data-testid={`${testid}-url-${i}`}
              />
              <div className="space-y-1">
                <Input
                  type="date"
                  value={(it.published_at || "").slice(0, 10)}
                  onChange={(e) => update(i, "published_at", e.target.value)}
                  disabled={!!it.live_page}
                  title="Date the claim/warning was published at the source"
                  data-testid={`${testid}-published-${i}`}
                />
                <label className="flex items-center gap-1.5 text-[11px] text-[#6C757D]">
                  <input
                    type="checkbox"
                    checked={!!it.live_page}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const next = items.map((row, j) => j === i ? { ...row, live_page: checked, published_at: checked ? "" : row.published_at } : row);
                      onChange(next);
                    }}
                    data-testid={`${testid}-livepage-${i}`}
                  />
                  Live page
                </label>
              </div>
              <div className="flex gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="px-2 text-sm text-[#6C757D] disabled:opacity-30">↑</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="px-2 text-sm text-[#6C757D] disabled:opacity-30">↓</button>
                <button type="button" onClick={() => remove(i)} className="px-2 text-sm text-clay" data-testid={`${testid}-remove-${i}`}>✕</button>
              </div>
            </div>
            <Textarea
              rows={2}
              value={it.description || ""}
              onChange={(e) => update(i, "description", e.target.value)}
              placeholder="Optional notes / context shown on the public brand page"
              data-testid={`${testid}-description-${i}`}
            />
            <Textarea
              rows={2}
              value={it.editor_note || ""}
              onChange={(e) => update(i, "editor_note", e.target.value)}
              placeholder="Editor's note (optional) — your editorial commentary shown alongside this entry on the public brand page"
              data-testid={`${testid}-editor-note-${i}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
